import { useEffect, useRef, useCallback } from "react";
import { 
  getLocalStorage, 
  setLocalStorage, 
  getLocalStorageJSON 
} from "../utils/storageCache";
import { getPreferredLanguage } from "../config/languages";

const MAX_RECONNECT_DELAY_MS = 30000;
const BASE_RECONNECT_DELAY_MS = 1000;

/**
 * CCMA WebSocket 生命周期。
 * enabled=false 时不建立连接（未开始训练时的大厅/群聊视图），避免登录后全局常驻占用 CCMA 会话。
 * 训练一旦开始，调用方应保持 enabled=true，切走页面也不拆会话，以便回来后继续对话。
 */
const useWebSocketLifecycle = ({
  isLoggedIn,
  enabled = true,
  ws,
  wsUrl,
  setSessionId,
  setUserId,
  setConnectionStatus,
  setIsSessionReady,
  setError,
  setTabsWidth,
  dividerRef,
  messageDispatchRef,
}) => {
  const isCleaningUpRef = useRef(false);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const connectRef = useRef(null);

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current != null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const reconnect = useCallback(() => {
    if (!enabledRef.current || isCleaningUpRef.current) return;
    clearReconnectTimer();
    const sock = ws.current;
    if (sock && (sock.readyState === WebSocket.OPEN || sock.readyState === WebSocket.CONNECTING)) {
      return;
    }
    reconnectAttemptRef.current = 0;
    setConnectionStatus("connecting");
    setError(null);
    if (connectRef.current) connectRef.current();
  }, [setConnectionStatus, setError, ws]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const generateSessionId = () =>
      "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });

    let storedSessionId = getLocalStorage("sessionId");
    if (!storedSessionId) {
      storedSessionId = generateSessionId();
      setLocalStorage("sessionId", storedSessionId);
    }
    setSessionId(storedSessionId);

    let storedUserId = getLocalStorage("userId");
    if (!storedUserId) {
      storedUserId = "user_" + Math.random().toString(36).substr(2, 9);
      setLocalStorage("userId", storedUserId);
    }
    setUserId(storedUserId);
  }, [isLoggedIn, setSessionId, setUserId]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (enabled) return;

    isCleaningUpRef.current = true;
    clearReconnectTimer();
    if (ws.current) {
      try {
        ws.current.close();
      } catch (_) {
        /* ignore */
      }
      ws.current = null;
    }
    setConnectionStatus("idle");
    setIsSessionReady(false);
  }, [isLoggedIn, enabled, ws, setConnectionStatus, setIsSessionReady]);

  useEffect(() => {
    if (!isLoggedIn || !enabled) return;

    const storedSessionId = getLocalStorage("sessionId");
    if (!storedSessionId) return;

    console.log("🔵 [EFFECT-MOUNT] CCMA WebSocket useEffect 挂载", {
      isLoggedIn,
      enabled,
      timestamp: new Date().toISOString(),
    });

    isCleaningUpRef.current = false;
    setConnectionStatus("connecting");

    let pendingInterfaceCount = 0;
    const hasHepIdToken = () => {
      const latestUserInfo = getLocalStorageJSON("userInfo", {});
      return !!latestUserInfo.hep_id_token;
    };
    const shouldTrackRequest = (url) => {
      const text = String(url || "");
      if (!text) return true;
      return !(
        text.includes("uap.hep.com.cn") ||
        text.includes("/otel/") ||
        text.includes("/qa/eventTracking")
      );
    };
    const flushInterfaceCount = () => {
      if (pendingInterfaceCount <= 0) return;
      if (!hasHepIdToken()) {
        pendingInterfaceCount = 0;
        return;
      }
      const socket = ws.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      const count = pendingInterfaceCount;
      pendingInterfaceCount = 0;
      socket.send(JSON.stringify({
        type: "hep_interface_cnt",
        count,
        timestamp: Date.now(),
      }));
    };
    const recordInterfaceCall = (url) => {
      if (!hasHepIdToken() || !shouldTrackRequest(url)) return;
      pendingInterfaceCount += 1;
      if (pendingInterfaceCount >= 5) {
        flushInterfaceCount();
      }
    };

    const originalFetch = window.fetch;
    if (typeof originalFetch === "function") {
      window.fetch = async (...args) => {
        try {
          return await originalFetch(...args);
        } finally {
          const request = args[0];
          recordInterfaceCall(request && request.url ? request.url : request);
        }
      };
    }

    const originalXHROpen = window.XMLHttpRequest && window.XMLHttpRequest.prototype.open;
    const originalXHRSend = window.XMLHttpRequest && window.XMLHttpRequest.prototype.send;
    if (originalXHROpen && originalXHRSend) {
      window.XMLHttpRequest.prototype.open = function (method, url, ...rest) {
        this.__hepTrackingUrl = url;
        return originalXHROpen.call(this, method, url, ...rest);
      };
      window.XMLHttpRequest.prototype.send = function (...args) {
        this.addEventListener("loadend", () => {
          recordInterfaceCall(this.__hepTrackingUrl);
        }, { once: true });
        return originalXHRSend.apply(this, args);
      };
    }

    const sendSessionInfo = (socket) => {
      const latestUserInfo = getLocalStorageJSON("userInfo", {});
      socket.send(
        JSON.stringify({
          type: "session_info",
          session_id: storedSessionId,
          user_id: latestUserInfo.username,
          unified_user_id: latestUserInfo.unified_user_id,
          user_type: latestUserInfo.user_type,
          name: latestUserInfo.name,
          class_number: latestUserInfo.class_number,
          faculty: latestUserInfo.faculty,
          school: latestUserInfo.school,
          language: getPreferredLanguage(),
          hep_id_token: latestUserInfo.hep_id_token || '',
        })
      );
    };

    const scheduleReconnect = () => {
      if (isCleaningUpRef.current || !enabledRef.current) return;
      const attempt = reconnectAttemptRef.current;
      const delay = Math.min(MAX_RECONNECT_DELAY_MS, BASE_RECONNECT_DELAY_MS * (2 ** attempt));
      reconnectAttemptRef.current = attempt + 1;
      setConnectionStatus("reconnecting");
      console.warn("[ccma-ws] 将在", delay, "ms 后重连, attempt=", attempt + 1);
      clearReconnectTimer();
      reconnectTimerRef.current = setTimeout(() => {
        if (isCleaningUpRef.current || !enabledRef.current) return;
        setConnectionStatus("connecting");
        connect();
      }, delay);
    };

    const connect = () => {
      if (isCleaningUpRef.current || !enabledRef.current) return;
      if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) {
        return;
      }
      const socket = new WebSocket(wsUrl);
      ws.current = socket;

      socket.onopen = () => {
        if (isCleaningUpRef.current || !enabledRef.current) return;
        console.log("WebSocket Connected");
        reconnectAttemptRef.current = 0;
        setConnectionStatus("connected");
        setIsSessionReady(false);
        setError(null);
        sendSessionInfo(socket);
      };

      socket.onmessage = (event) => {
        if (isCleaningUpRef.current || !enabledRef.current) return;
        const data = JSON.parse(event.data);
        if (messageDispatchRef.current) {
          messageDispatchRef.current(data);
        }
      };

      socket.onerror = (error) => {
        console.error("WebSocket error:", error);
      };

      socket.onclose = (event) => {
        console.log("WebSocket连接已断开:", event.code, event.reason);
        console.warn("[ccma-ws] disconnect", {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          isCleanup: isCleaningUpRef.current,
          enabled: enabledRef.current,
          时间: new Date().toLocaleString(),
        });
        if (ws.current === socket) {
          ws.current = null;
        }
        if (isCleaningUpRef.current || !enabledRef.current) {
          console.log("🔇 [onclose] cleanup 或视图切换触发的关闭，不弹错误提示");
          return;
        }
        setConnectionStatus("reconnecting");
        scheduleReconnect();
      };
    };

    connectRef.current = connect;
    connect();

    const interfaceCountInterval = setInterval(flushInterfaceCount, 5000);
    const heartbeatInterval = setInterval(() => {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        ws.current.send(JSON.stringify({ type: "ping", timestamp: Date.now() }));
      } else if (!ws.current || ws.current.readyState === WebSocket.CLOSED) {
        console.error("❌ 心跳检测：连接已关闭");
      }
    }, 30000);

    return () => {
      console.log("🔴 [CLEANUP] WebSocket cleanup 触发", {
        readyState: ws.current?.readyState,
        timestamp: new Date().toISOString(),
      });
      isCleaningUpRef.current = true;
      clearReconnectTimer();
      clearInterval(heartbeatInterval);
      clearInterval(interfaceCountInterval);
      flushInterfaceCount();
      if (typeof originalFetch === "function") {
        window.fetch = originalFetch;
      }
      if (originalXHROpen && originalXHRSend) {
        window.XMLHttpRequest.prototype.open = originalXHROpen;
        window.XMLHttpRequest.prototype.send = originalXHRSend;
      }
      if (ws.current) {
        try {
          ws.current.close();
        } catch (_) {
          /* ignore */
        }
        ws.current = null;
      }
    };
  }, [
    isLoggedIn,
    enabled,
    messageDispatchRef,
    setConnectionStatus,
    setError,
    setIsSessionReady,
    ws,
    wsUrl,
  ]);

  useEffect(() => {
    const divider = dividerRef.current;
    if (!divider) return;

    let isDragging = false;

    const onMouseDown = (e) => {
      isDragging = true;
      e.preventDefault();
    };
    const onMouseMove = (e) => {
      if (!isDragging) return;
      const newWidth = e.clientX - divider.getBoundingClientRect().left;
      setTabsWidth(Math.max(100, Math.min(newWidth, window.innerWidth * 0.4)));
    };
    const onMouseUp = () => {
      isDragging = false;
    };

    divider.addEventListener("mousedown", onMouseDown, { passive: true });
    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("mouseup", onMouseUp, { passive: true });

    return () => {
      divider.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [dividerRef, setTabsWidth]);

  return { reconnect };
};

export default useWebSocketLifecycle;
